import { Button } from "@freightsimple/ui-core/Controls/Button";
import { T } from "@freightsimple/ui-core/Typography/T";
import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CurrencyTag } from "@/Shared/Components/CurrencyTag";
import { useShipmentDraftsApi } from "@/Hooks/useShipmentDraftsApi";
import { useWebhooksApi } from "@/Hooks/useWebhooksApi";
import { showDeveloperAffordances } from "@/Helpers/developerMode";
import { showPairMobileDevice } from "@/Helpers/showPairMobileDevice";
import { isDemo } from "@/Environment";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { ReferralCodeDialog } from "@/Features/Referral/Exports/Dialogs/ReferralCodeDialog";
import { NavBarPotentialCoupon } from "@/Features/Nav/CustomComponents/NavBarPotentialCoupon";
import { NavBarInvoiceWarning } from "@/Features/Nav/CustomComponents/NavBarInvoiceWarning";
import { useNavBarDemoLabelHidden } from "../Hooks/useNavBarDemoLabelHidden";
import { NavBarTopBar } from "../Bars/NavBarTopBar";

// Employee-only dev tools, lazily loaded so customers never download dev-tools.
const NavBarDashboardDevMenuSection = lazy(
  () => import("./NavBarDashboardDevMenuSection"),
);

const HELP_CENTER_URL = "https://help.freightsimple.com";

/**
 * Wires the presentational NavBarTopBar to the app's real data — auth, coupon,
 * invoice warning, drafts, and the referral / switch-organization modals. The
 * coupon and invoice-warning components are self-fetching, so they pass through
 * as slots.
 */
// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function NavBarTopContainer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthentication();
  const [referralCodeOpen, setReferralCodeOpen] = useState(false);
  const shipmentDraftsApi = useShipmentDraftsApi();
  const webhooksApi = useWebhooksApi();
  const { isDemoHidden, setDemoLabelHidden } = useNavBarDemoLabelHidden();

  const [showDrafts, setShowDrafts] = useState(false);
  useEffect(() => {
    async function fetchDraftsSummary() {
      try {
        const summary = await shipmentDraftsApi.getShipmentDraftsSummary();
        if (summary.count > 0) {
          setShowDrafts(true);
        }
      } catch (e) {
        console.error(e);
      }
    }
    fetchDraftsSummary();
  }, []);

  // Same shape as drafts: the Developers group appears once the company has a
  // webhook, or delivery history from one it deleted. Nothing on the user
  // object says so.
  const [showDevelopers, setShowDevelopers] = useState(false);
  useEffect(() => {
    async function fetchWebhooks() {
      try {
        const { webhooks, hasRecentDeliveries } =
          await webhooksApi.listCompanyWebhooks();
        if (webhooks.length > 0 || hasRecentDeliveries) {
          setShowDevelopers(true);
        }
      } catch (e) {
        console.error(e);
      }
    }
    fetchWebhooks();
  }, []);

  const isLinkedCompany = user?.isLinkedCompany ?? false;
  const currency = isLinkedCompany ? user?.company?.currency : undefined;
  const showDevTools = showDeveloperAffordances(user?.email);
  // Hidden from customers in production until the iOS app ships (ENG-6029).
  const showPairMobile = showPairMobileDevice(user?.email);

  const demoLabel =
    isDemo() && !isDemoHidden ? (
      <Button size="link" inheritFont onClick={() => setDemoLabelHidden(true)}>
        <T size="xs" weight="light" color="muted">
          Demo
        </T>
      </Button>
    ) : undefined;

  // The get-quotes CTA hides during the booking flow.
  const isBooking =
    location.pathname.startsWith("/book/") ||
    location.pathname === "/booking-success";

  return (
    <>
      <ReferralCodeDialog
        open={referralCodeOpen}
        onOpenChange={setReferralCodeOpen}
      />
      <NavBarTopBar
        activePath={location.pathname}
        onNavigate={(path) => navigate(path)}
        onHelpCenter={() => window.open(HELP_CENTER_URL, "_new")}
        showDrafts={showDrafts}
        showDevelopers={showDevelopers}
        showPairMobile={showPairMobile}
        showGetQuotes={!isBooking}
        onGetQuotes={() => {
          setTimeout(() => navigate("/book/details"), 300);
        }}
        onReferralCode={() => setReferralCodeOpen(true)}
        userName={user?.name ?? ""}
        companyName={user?.companyName ?? ""}
        currencyTag={currency ? <CurrencyTag currency={currency} /> : undefined}
        coupon={<NavBarPotentialCoupon />}
        invoiceWarning={<NavBarInvoiceWarning />}
        demoLabel={demoLabel}
        devMenu={
          showDevTools ? (
            <Suspense fallback={<></>}>
              <NavBarDashboardDevMenuSection />
            </Suspense>
          ) : undefined
        }
      />
    </>
  );
}
